<template>
    <div class="tasks-page">
        <div class="tasks-layout">
            <!-- task list -->
            <aside class="tl-card">
                <div class="tl-head">
                    <div class="tl-title">
                        <span class="tl-title-ic"><v-icon small>mdi-clipboard-check-outline</v-icon></span>
                        <span>{{ $t('Tasks') }}</span>
                    </div>
                    <span class="tl-count">{{ totalCount }}</span>
                </div>

                <div class="tl-body">
                    <v-overlay :absolute="absolute" :value="loading" color="#ffffff" opacity=".65">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-overlay>

                    <div v-if="tasks.length == 0 && !loading" class="tl-empty">
                        <v-icon size="40" color="#b8c4c1">mdi-check-all</v-icon>
                        <span>{{ $t('NoItemsToShow') }}</span>
                    </div>

                    <button v-for="item in tasks"
                            :key="item.activityInstanceId"
                            type="button"
                            class="tl-item"
                            :class="{ 'is-unread': !item.claimed, 'is-active': taskId === item.activityInstanceId, 'is-late': item.isDelayed }"
                            :disabled="itemDisabled"
                            @click="showData(item)">
                        <span class="tl-item-bar" aria-hidden="true"></span>
                        <div class="tl-item-top">
                            <span class="tl-item-title">{{ item.title }}</span>
                            <span class="tl-item-id">#{{ item.activityInstanceId }}</span>
                        </div>
                        <div class="tl-item-process">{{ item.processTitle | stripProcessTitleSuffix }}</div>
                        <!-- عنوان الطلب — absent on requests submitted before the field existed -->
                        <div v-if="item.requestTitle" class="tl-item-req">{{ item.requestTitle }}</div>
                        <div class="tl-item-meta">
                            <span class="tl-ref">{{ item.referenceNumber }}</span>
                            <span class="tl-date" :class="{ 'is-late': item.isDelayed }">
                                <v-icon x-small class="tl-date-ic">mdi-calendar-clock</v-icon>
                                <span class="tl-date-lbl">{{ $t('DueDate') }}</span>
                                {{ item.dueDate | formatDateGregorian }}
                            </span>
                            <span class="tl-date tl-date--start">
                                <span class="tl-date-lbl">{{ $t('StartDate') }}</span>
                                {{ item.plannedStartDate | formatDateGregorian }}
                            </span>
                        </div>
                    </button>
                </div>

                <div v-if="totalPages > 1" class="tl-foot">
                    <v-pagination v-model="page"
                                  class="tl-pagination"
                                  :length="totalPages"
                                  total-visible="5"
                                  circle
                                  next-icon="mdi-menu-right"
                                  prev-icon="mdi-menu-left"
                                  @input="handlePageChange" />
                </div>
            </aside>

            <!-- task details -->
            <section class="tasks-detail">
                <template v-if="show">
                    <Dynamic v-if="isDynamic && taskId" :task-id="taskId" @completed="activityCompleted" />
                    <SurveyApproval v-if="isSurveyApproval && taskId" :task-id="taskId" @completed="activityCompleted" />
                </template>
                <div v-else class="tasks-placeholder">
                    <span class="tasks-placeholder-ic"><v-icon size="30">mdi-gesture-tap</v-icon></span>
                    <span>{{ $t('NoItemsToShow') }}</span>
                </div>
            </section>
        </div>
    </div>
</template>
<script>

    import Dynamic from '@/components/app/task/dynamic/TaskDetails.vue'
    import SurveyApproval from '@/components/app/task/survey/TaskDetails.vue'

    import { connection } from '@/plugins/hub/myHubPlugin.js'
    import TasksService from '@/services/TasksService.js'
    import { ProcessTypesEnum } from '@/helpers/constants/enumerations.js'

    export default {

        components: {
            Dynamic, SurveyApproval
        },

        data() {

            return {
                show: false,
                itemDisabled: false,
                loading: false,
                selected: [2],
                tasks: [],
                taskId: null,
                totalCount: 0,
                page: 1,
                totalPages: 0,
                pageSize: 7,
                processType: null,
                absolute: true,
            }

        },

        computed: {
            isDynamic() {
                return !this.isSurveyApproval
            },

            isSurveyApproval() {
                return this.processType == ProcessTypesEnum.SurveyApproval
            }
        },

        watch: {
            // Clicking a notification while this page is already open only changes the query,
            // so the route is reused and created() never runs again. Without this the second
            // and later clicks did nothing at all until the page was reloaded by hand.
            '$route.query.taskId': function () {
                this.openTaskFromQuery()
            }
        },

        created() {
            this.loadTasks()
        },

        mounted() {
            connection.on('ReceiveChanges', () => {
                this.loadTasks()
            })
        },

        methods: {
            loadTasks() {
                this.loading = true
                TasksService.listTasks(this.page, this.pageSize).then((res) => {
                    this.tasks = res.data.data
                    this.totalCount = res.data.total
                    this.totalPages = Math.ceil(res.data.total / this.pageSize)
                    this.openTaskFromQuery()
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            // Entry point for the notifications dropdown, which links to /tasks?taskId=<id>.
            // Opening it here rather than in the dropdown keeps one path to showData, so the
            // task is claimed and rendered exactly as it is when picked from this list.
            openTaskFromQuery() {
                const requested = Number(this.$route.query.taskId)
                if (!requested || this.taskId === requested) {
                    return
                }
                const index = this.tasks.findIndex(task => task.activityInstanceId === requested)
                if (index > -1) {
                    this.selected = index
                    this.showData(this.tasks[index])
                    return
                }
                // the task can sit on another page of the list; showData only needs its id,
                // and claimTask just flags the object it is handed
                this.showData({ activityInstanceId: requested, claimed: false })
            },

            showData(activity) {
                this.claimTask(activity)
                if (this.taskId != activity.activityInstanceId) {
                    this.show = false
                    TasksService.getProcesType(activity.activityInstanceId).then(res => {
                        this.taskId = activity.activityInstanceId
                        this.processType = res.data
                        this.show = true
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            handlePageChange(value) {
                this.page = value
                this.loadTasks()
            },

            claimTask(activity) {
                activity.claimed = true
                TasksService.claimActivity(activity.activityInstanceId).then(res => {
                    // the header owns the unread badge and cannot see this call; it re-reads
                    // the count rather than adjusting it, so this stays correct even when the
                    // hub delivers its own ReceiveClaim for the same activity
                    this.$root.$emit('task-claimed')
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            activityCompleted(success) {
                if (success) {
                    this.loadTasks()
                    this.taskId = null
                    this.processType = null
                }
            },

        },
    }
</script>
<style scoped>
    /* Tokens (Soft UI, shared with the rating controls) */
    .tasks-page {
        --tk-surface: #f3f6f5;
        --tk-card: #ffffff;
        --tk-line: #e6ecea;
        --tk-text: #1f2a28;
        --tk-muted: #6b7a77;
        --tk-primary: #0a716e;
        --tk-primary-tint: #e6f2f1;
        --tk-danger: #dc2626;
        --tk-radius: 18px;
        --tk-ease: cubic-bezier(.2, .8, .2, 1);
        --tk-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18);

        background: #fff;
        min-height: calc(100vh - 120px);
        padding: 18px 20px 28px;
    }

    .tasks-layout {
        display: grid;
        grid-template-columns: 360px minmax(0, 1fr);
        gap: 18px;
        align-items: start;
    }

    /* ---------- list ---------- */
    .tl-card {
        position: sticky;
        top: 84px;
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - 110px);
        background: var(--tk-card);
        border: 1px solid var(--tk-line);
        border-radius: var(--tk-radius);
        box-shadow: var(--tk-shadow);
        overflow: hidden;
    }

    .tl-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 18px 14px;
        border-bottom: 1px solid var(--tk-line);
    }

    .tl-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 17px;
        font-weight: 800;
        color: var(--tk-text);
    }

    .tl-title-ic {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 11px;
        background: var(--tk-primary-tint);
    }
    .tl-title-ic .v-icon { color: var(--tk-primary) !important; }

    .tl-count {
        min-width: 32px;
        padding: 0 10px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: var(--tk-primary);
        color: #fff;
        font-size: 13px;
        font-weight: 700;
    }

    .tl-body {
        position: relative;
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-height: 200px;
    }

    .tl-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 40px 12px;
        color: var(--tk-muted);
        font-size: 13.5px;
        font-weight: 600;
    }

    .tl-item {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 5px;
        width: 100%;
        text-align: start;
        padding: 12px 14px 12px;
        border: 1px solid transparent;
        border-radius: 14px;
        background: var(--tk-surface);
        color: var(--tk-text);
        font: inherit;
        cursor: pointer;
        transition: background-color .2s var(--tk-ease), border-color .2s var(--tk-ease), box-shadow .2s var(--tk-ease), transform .2s var(--tk-ease);
        -webkit-tap-highlight-color: transparent;
    }
    .tl-item:hover { background: #eef3f2; }
    .tl-item:focus { outline: none; }
    .tl-item:focus-visible { box-shadow: 0 0 0 2px var(--tk-card), 0 0 0 4px var(--tk-primary); }

    .tl-item-bar {
        position: absolute;
        inset-inline-start: 0;
        top: 12px;
        bottom: 12px;
        width: 3px;
        border-radius: 3px;
        background: transparent;
        transition: background-color .2s var(--tk-ease);
    }
    .tl-item.is-unread .tl-item-bar { background: var(--tk-primary); }
    .tl-item.is-late .tl-item-bar { background: var(--tk-danger); }

    .tl-item.is-active {
        background: var(--tk-card);
        border-color: var(--tk-primary);
        box-shadow: 0 8px 20px -14px var(--tk-primary);
    }

    .tl-item-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
    }

    .tl-item-title {
        font-size: 14px;
        font-weight: 700;
        line-height: 1.35;
        color: var(--tk-text);
    }
    .tl-item.is-unread .tl-item-title { font-weight: 800; }

    .tl-item-id {
        flex: 0 0 auto;
        font-size: 11px;
        font-weight: 600;
        color: var(--tk-muted);
        padding: 2px 7px;
        border-radius: 999px;
        background: rgba(15, 23, 42, .05);
    }

    .tl-item-process {
        font-size: 12.5px;
        color: var(--tk-muted);
        line-height: 1.45;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .tl-item-req {
        font-size: 12.5px;
        font-weight: 600;
        color: var(--tk-text);
        line-height: 1.45;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .tl-item-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px 10px;
        margin-top: 4px;
    }

    .tl-ref {
        font-size: 11.5px;
        font-weight: 700;
        color: var(--tk-primary);
        background: var(--tk-primary-tint);
        padding: 3px 9px;
        border-radius: 999px;
        letter-spacing: .01em;
    }

    .tl-date {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11.5px;
        font-weight: 600;
        color: var(--tk-muted);
    }
    .tl-date-lbl { font-weight: 500; opacity: .85; }
    .tl-date-ic { color: var(--tk-muted) !important; }
    .tl-date.is-late { color: var(--tk-danger); }
    .tl-date.is-late .tl-date-ic { color: var(--tk-danger) !important; }
    .tl-date--start { opacity: .8; }

    .tl-foot {
        display: flex;
        justify-content: center;
        padding: 8px 10px 10px;
        border-top: 1px solid var(--tk-line);
    }
    .tl-pagination ::v-deep .v-pagination__item,
    .tl-pagination ::v-deep .v-pagination__navigation {
        box-shadow: none;
        font-weight: 600;
        color: var(--tk-muted);
    }
    .tl-pagination ::v-deep .v-pagination__item--active {
        background: var(--tk-primary) !important;
        color: #fff;
    }

    /* ---------- detail ---------- */
    .tasks-detail {
        background: transparent !important;
        min-width: 0;
        min-height: 60vh;
    }

    .tasks-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        min-height: 60vh;
        border: 1px dashed #cfd9d6;
        border-radius: var(--tk-radius);
        color: var(--tk-muted);
        font-size: 14px;
        font-weight: 600;
    }
    .tasks-placeholder-ic {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: var(--tk-card);
        box-shadow: var(--tk-shadow);
    }
    .tasks-placeholder-ic .v-icon { color: var(--tk-primary) !important; }

    @media (max-width: 1263px) {
        .tasks-layout { grid-template-columns: 1fr; }
        .tl-card { position: static; max-height: none; }
        .tl-body { max-height: 420px; }
    }

    @media (max-width: 600px) {
        .tasks-page { padding: 12px 12px 24px; }
    }

    @media (prefers-reduced-motion: reduce) {
        .tl-item, .tl-item-bar { transition: none; }
    }
</style>
